/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `CompanyEdit` for the Caintra Connect frontend.
 * @module pages/CompanyEdit
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/CompanyEdit.module.css";

import { AppContext } from "../context/AppContext";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import ProgressBar from "../components/ProgressBar";
import SectionTabs from "../components/SectionTabs";
import SectionContent from "../components/SectionContent";
import SaveSection from "../components/SaveSection";

import { CircularProgress, Backdrop } from "@mui/material";
import { ReactComponent as ChevronLeftIcon } from "../assets/icons/CompanyEditPage/ChevronLeftIcon.svg";
import { ReactComponent as TitleIcon } from "../assets/icons/CompanyEditPage/TitleIcon.svg";

import { use, useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useNavigation } from "../context/NavigationContext";
import {
  getUserPermissions,
  getCompanyById,
  calculateProgressStep,
  splitFormDataForUpdate,
  updateCompany,
  uploadCompanyFiles,
  DOCUMENT_KEYS,
} from "../logic/companyEdit";
import { isProfileEditableField, getProfileByNumber } from "../utils/common";

const CompanyEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const location = useLocation();
  const { changePermissions } = use(AppContext);
  const { updateNavigation } = useNavigation();

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("informacion-general");
  const [hasChanges, setHasChanges] = useState(false);
  const [formData, setFormData] = useState({});
  const [currentStep, setCurrentStep] = useState(0); // 0-based index, so 2 means the third step is current
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const statusMessage = "* " + t("pages.companyEdit.statusMessage");
  const sections = [
    { id: "informacion-general", label: t("components.sectionsTabs.section1") },
    { id: "contacto-ubicacion", label: t("components.sectionsTabs.section2") },
    { id: "productos-servicios", label: t("components.sectionsTabs.section3") },
    { id: "redes-presencia", label: t("components.sectionsTabs.section4") },
    { id: "vinculacion-networking", label: t("components.sectionsTabs.section5") },
  ];

  // Selected profile from navigation state (0 = main, 1 = profile 2, 2 = profile 3)
  const selectedProfile = location.state?.selectedProfile ?? 0;

  // Memoize the validation change handler to prevent unnecessary re-renders
  const handleValidationChange = useCallback((hasErrors) => {
    setHasValidationErrors(hasErrors);
  }, []);

  // Progress bar steps
  const steps = [
    { label: t("components.progressBar.step1") },
    { label: t("components.progressBar.step2") },
    { label: t("components.progressBar.step3") },
    { label: t("components.progressBar.step4") },
  ];

  const calculateProgress = () =>
    calculateProgressStep(company, selectedProfile);

  useEffect(() => {
    if (company) {
      setCurrentStep(calculateProgress());
    }
  }, [company]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!!token && token.trim() !== "undefined") {
      loadData();
    } else {
      logger.info(
        "Error trying to get the token to retrieve the company data.",
      );
    }
  }, [companyId]);

  useEffect(() => {
    updateNavigation("/companyEdit", location.state?.context || "myCompany");
  }, [location.state?.context]);

  const loadData = async () => {
    try {
      const userId = localStorage.getItem("userId");
      const permisos = await getUserPermissions(userId, navigate);
      if (permisos != null) {
        changePermissions(permisos);
      }
      const data = await getCompanyById(companyId, navigate);
      if (!data) {
        setCompany(null);
        navigate("/home", { replace: true });
        return;
      }

      // Apply selectedProfile from navigation state if provided
      const selectedProfileFromState = location.state?.selectedProfile;
      if (
        selectedProfileFromState !== undefined &&
        selectedProfileFromState !== null
      ) {
        data.perfilSeleccionado = selectedProfileFromState;
      }

      setCompany(data);
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    const from = location.state?.from;
    const context = location.state?.context;
    const selectedProfileFromState = location.state?.selectedProfile;

    if (from === "companyProfile") {
      navigate("/companyProfile", {
        state: {
          company,
          context: context || "myCompany",
          selectedProfile: selectedProfileFromState,
        },
      });
    } else {
      navigate("/companyCatalog", { context: "directory" });
    }
  };

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
  };

  const handleFieldChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
    // Also update the company state so inputs reflect the changes immediately.
    // For profile-editable fields when editing an additional profile, update
    // the profile object so getCompanyFieldValue() reads the new value.
    setCompany((prev) => {
      if (selectedProfile > 0 && isProfileEditableField(field)) {
        const profile = getProfileByNumber(prev, selectedProfile);
        if (!profile) {
          return { ...prev, [field]: value };
        }
        const profileIndex = prev.perfiles.findIndex(
          (p) => p?.numeroDePerfil === selectedProfile,
        );
        if (profileIndex < 0) {
          return { ...prev, [field]: value };
        }
        const updatedPerfiles = [...prev.perfiles];
        updatedPerfiles[profileIndex] = {
          ...updatedPerfiles[profileIndex],
          [field]: value,
        };
        return { ...prev, perfiles: updatedPerfiles };
      }
      return { ...prev, [field]: value };
    });

    // Include null (e.g. clearing a removed contact) so Save stays enabled and splitFormDataForUpdate can send clears
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (hasValidationErrors) {
      logger.info("Cannot save: validation errors present");
      return;
    }

    setSaving(true);
    try {
      const { regularFields, fileFields } = splitFormDataForUpdate(
        formData,
        DOCUMENT_KEYS,
      );

      if (
        Object.keys(regularFields).length > 0 ||
        Object.keys(fileFields).length > 0
      ) {
        const data = await updateCompany(
          companyId,
          regularFields,
          fileFields,
          navigate,
          selectedProfile,
        );

        setCompany({
          ...data.company,
          _lastUpdate: Date.now(),
        });

        if (data.presignedUrls && Object.keys(fileFields).length > 0) {
          await uploadCompanyFiles(data.presignedUrls, fileFields);
        }
      }

      setHasChanges(false);
    } catch (error) {
      logger.error("Error saving changes:", { err: sanitizeError(error) });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.languages}>
            <NotificationBell navigate={navigate} />
            <LanguageSelector />
          </div>
          <div className={styles.headerInfoContainer}>
            <div className={styles.headerInfo}>
              <button
                className={styles.returnButtonContainer}
                onClick={handleBack}
              >
                <ChevronLeftIcon className={styles.returnButtonIcon} />
                <span className={styles.returnButtonText}>
                  {t("pages.companyEdit.returnButton")}
                </span>
              </button>
              <div className={styles.titleContainer}>
                <TitleIcon className={styles.titleIcon} />
                <h1 className={styles.title}>
                  {t("pages.companyEdit.header", {
                    profileNumber: selectedProfile + 1,
                  })}
                </h1>
              </div>
            </div>
            <div className={styles.progressContainer}>
              <ProgressBar steps={steps} currentStep={currentStep} />
              <div className={styles.statusMessage}>{statusMessage}</div>
            </div>
          </div>
        </div>
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          }}
          open={loading}
        >
          <CircularProgress
            size={60}
            thickness={4}
            sx={{
              color: "#4eabe7",
            }}
          />
        </Backdrop>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        {/* Page title and back button */}
        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <button
              className={styles.returnButtonContainer}
              onClick={handleBack}
            >
              <ChevronLeftIcon className={styles.returnButtonIcon} />
              <span className={styles.returnButtonText}>
                {t("pages.companyEdit.returnButton")}
              </span>
            </button>
            <div className={styles.titleContainer}>
              <TitleIcon className={styles.titleIcon} />
              <h1 className={styles.title}>
                {t("pages.companyEdit.header", {
                  profileNumber: selectedProfile + 1,
                })}
              </h1>
            </div>
          </div>
          <div className={styles.progressContainer}>
            <ProgressBar steps={steps} currentStep={currentStep} />
            {currentStep < steps.length && (
              <div className={styles.statusMessage}>{statusMessage}</div>
            )}
          </div>
        </div>
      </div>

      {/* Section Tabs */}
      <SectionTabs sections={sections} onSectionChange={handleSectionChange} />

      {/* Tablet visibility notice */}
      <div className={styles.visibilityNotice}>
        {t("pages.companyEdit.statusMessage")}
      </div>

      {/* Section Content */}
      <SectionContent
        activeSection={activeSection}
        company={company}
        onFieldChange={handleFieldChange}
        onValidationChange={handleValidationChange}
        selectedProfile={selectedProfile}
      />

      {/* Save Section */}
      <SaveSection
        onSave={handleSave}
        hasChanges={hasChanges}
        loading={saving}
        hasValidationErrors={hasValidationErrors}
      />
    </div>
  );
};

export default CompanyEdit;
